import { alpha, styled } from '@mui/material';

export const TableRoot = styled('div')({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	overflowX: 'auto',
});

export const TableContainer = styled('div')({
	width: '100%',
	flex: '1 1 auto',
	minHeight: 120,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
});

export const TableHeader = styled('div')({
	flex: '0 0 auto',
	overflow: 'hidden',
});

export const TableViewport = styled('div')({
	position: 'relative',
	minHeight: 0,
	flex: '1 1 auto',
});

export const PagedTableViewport = styled('div', {
	shouldForwardProp: (prop) => prop !== 'pageSize',
})<{ pageSize: number }>(({ pageSize }) => ({
	height: pageSize * 47,
	minHeight: pageSize * 47,
	flex: '0 0 auto',
	overflow: 'auto',
}));

export const TableSummary = styled('div')(({ theme }) => ({
	minHeight: 48,
	padding: '0 18px',
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	color: theme.palette.novotalk.$textColorDisabled,
	backgroundColor: theme.palette.novotalk.$resourcesInputBgColor,
	fontSize: 13.5,
	fontWeight: 600,
}));

export const TablePagination = styled('div')(({ theme }) => ({
	minHeight: 56,
	padding: '0 18px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	borderTop: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const TablePaginationCount = styled('span')(({ theme }) => ({
	color: theme.palette.novotalk.$textColorDisabled,
	fontSize: 13,
	fontWeight: 600,
}));

export const ClearFiltersButton = styled('button')(({ theme }) => ({
	height: 28,
	padding: '0 11px',
	border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	borderRadius: 9,
	color: theme.palette.novotalk.$activeColor,
	backgroundColor: theme.palette.novotalk.$backgroundColor,
	fontSize: 12.5,
	fontWeight: 600,
	cursor: 'pointer',
}));

export const StyledTable = styled('table')(({ theme }) => ({
	width: '100%',
	borderCollapse: 'separate',
	borderSpacing: 0,
	tableLayout: 'fixed',
	color: theme.palette.novotalk.$textColorDarkBlue,
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const HeaderRow = styled('tr')(({ theme }) => ({
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const HeaderCell = styled('th')(({ theme }) => ({
	height: 43,
	padding: '0 16px',
	boxSizing: 'border-box',
	borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	color: theme.palette.novotalk.$textColorDisabled,
	fontWeight: 600,
	textAlign: 'start',
	verticalAlign: 'middle',
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const HeaderButton = styled('button', {
	shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active, theme }) => ({
	minWidth: 0,
	padding: 0,
	border: 0,
	display: 'flex',
	gap: 6,
	alignItems: 'center',
	color: active ? theme.palette.novotalk.$activeColor : 'inherit',
	font: 'inherit',
	textTransform: 'inherit',
	textAlign: 'inherit',
	background: 'transparent',
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${theme.palette.novotalk.$activeColor}`,
		outlineOffset: 2,
	},
}));

export const HeaderContent = styled('div')({
	minWidth: 0,
	display: 'flex',
	alignItems: 'center',
	gap: 6,
});

export const HeaderTitle = styled('span')({
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

export const SortIndicator = styled('span', {
	shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active, theme }) => ({
	flex: '0 0 auto',
	color: active ? theme.palette.novotalk.$activeColor : 'currentColor',
	fontSize: 9,
}));

export const FilterRow = styled('tr')(({ theme }) => ({
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const FilterCell = styled('th')(({ theme }) => ({
	padding: '8px 12px',
	boxSizing: 'border-box',
	borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const FilterInput = styled('input')(({ theme }) => ({
	width: '100%',
	height: 32,
	padding: '0 10px',
	boxSizing: 'border-box',
	border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	borderRadius: 5,
	outline: 'none',
	color: theme.palette.novotalk.$textColorDarkBlue,
	backgroundColor: theme.palette.novotalk.$resourcesInputBgColor,
	'&:focus': {
		borderColor: theme.palette.novotalk.$activeColor,
	},
}));

export const FilterIconButton = styled('button', {
	shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active, theme }) => ({
	width: 24,
	height: 24,
	flex: '0 0 24px',
	padding: 0,
	border: 0,
	borderRadius: 6,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: active ? theme.palette.novotalk.$activeColor : '#c3ccd8',
	backgroundColor: active ? alpha(theme.palette.novotalk.$activeColor, 0.14) : 'transparent',
	cursor: 'pointer',
	'& svg': { fontSize: 15 },
	'&:hover': { color: theme.palette.novotalk.$activeColor, backgroundColor: alpha(theme.palette.novotalk.$activeColor, 0.1) },
}));

export const FilterMenu = styled('div')({
	width: 250,
	maxHeight: 360,
	padding: 8,
	overflowY: 'auto',
	boxSizing: 'border-box',
});

export const FilterSearch = styled('input')(({ theme }) => ({
	width: '100%',
	height: 34,
	marginBottom: 6,
	padding: '0 10px',
	boxSizing: 'border-box',
	border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	borderRadius: 8,
	outline: 'none',
	color: theme.palette.novotalk.$textColorDarkBlue,
	fontSize: 13,
	'&:focus': { borderColor: theme.palette.novotalk.$activeColor },
}));

export const FilterOptionButton = styled('button')(({ theme }) => ({
	width: '100%',
	minHeight: 36,
	padding: '3px 5px 3px 0',
	border: 0,
	borderRadius: 8,
	display: 'flex',
	alignItems: 'center',
	color: theme.palette.novotalk.$textColorDarkBlue,
	backgroundColor: 'transparent',
	textAlign: 'left',
	cursor: 'pointer',
	'&:hover': { backgroundColor: theme.palette.novotalk.$activityReadBackgroundColorHover },
	'& .MuiCheckbox-root': { padding: 7, color: '#cfd8e3' },
	'& .Mui-checked': { color: `${theme.palette.novotalk.$activeColor} !important` },
}));

export const FilterLabel = styled('span')({
	display: 'block',
	fontSize: 13.5,
	fontWeight: 500,
});

export const FilterSubLabel = styled('span')(({ theme }) => ({
	display: 'block',
	padding: '2px 8px',
	color: theme.palette.novotalk.$textColorDisabled,
	fontSize: 11.5,
}));

export const ClearFilterButton = styled('button')(({ theme }) => ({
	width: '100%',
	marginTop: 8,
	padding: 8,
	border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	borderRadius: 8,
	color: theme.palette.novotalk.$textColorDisabled,
	backgroundColor: theme.palette.novotalk.$resourcesInputBgColor,
	fontSize: 12.5,
	fontWeight: 600,
	cursor: 'pointer',
}));

export const DateFields = styled('div')(({ theme }) => ({
	marginTop: 6,
	paddingTop: 8,
	borderTop: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	'& label': { display: 'flex', alignItems: 'center', gap: 8 },
	'& span': {
		width: 36,
		color: theme.palette.novotalk.$textColorDisabled,
		fontSize: 10.5,
		fontWeight: 700,
		textTransform: 'uppercase',
	},
	'& input': {
		minWidth: 0,
		height: 32,
		flex: 1,
		padding: '0 8px',
		border: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
		borderRadius: 8,
		color: theme.palette.novotalk.$textColorDarkBlue,
	},
}));

export const BodyRow = styled('tr', {
	shouldForwardProp: (prop) => prop !== 'clickable' && prop !== 'selected',
})<{ clickable: boolean; selected: boolean }>(({ clickable, selected, theme }) => ({
	cursor: clickable ? 'pointer' : 'default',
	backgroundColor: selected
		? theme.palette.novotalk.$activityReadBackgroundColorHover
		: theme.palette.novotalk.$backgroundColor,
	boxShadow: selected ? `inset 3px 0 0 ${theme.palette.novotalk.$activeColor}` : undefined,
	'&:hover': clickable ? {
		backgroundColor: theme.palette.novotalk.$activityReadBackgroundColorHover,
	} : undefined,
	'&:focus-visible': clickable ? {
		outline: `2px solid ${theme.palette.novotalk.$activeColor}`,
		outlineOffset: -2,
	} : undefined,
}));

export const BodyCell = styled('td')(({ theme }) => ({
	height: 47,
	padding: '8px 16px',
	boxSizing: 'border-box',
	overflow: 'hidden',
	borderBottom: `1px solid ${theme.palette.novotalk.$resourcesTableBorderColor}`,
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontSize: 14,
	color: '#17284a',
}));

export const Status = styled('div', {
	shouldForwardProp: (prop) => prop !== 'overlay',
})<{ overlay: boolean }>(({ overlay, theme }) => ({
	position: 'absolute',
	left: 0,
	right: 0,
	...(overlay ? {
		top: 0,
		bottom: 0,
	} : {
		bottom: 0,
		minHeight: 48,
	}),
	padding: 16,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	zIndex: 2,
	color: theme.palette.novotalk.$textColorDisabled,
	backgroundColor: theme.palette.novotalk.$backgroundColor,
}));

export const RetryButton = styled('button')(({ theme }) => ({
	marginLeft: 12,
	padding: '6px 12px',
	border: `1px solid ${theme.palette.novotalk.$activeColor}`,
	borderRadius: 4,
	color: theme.palette.novotalk.$activeColor,
	background: 'transparent',
	cursor: 'pointer',
}));
